import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export const ShopByCategory = ({ categories, products }) => {
  const mobileScrollRef = useRef(null);
  const desktopScrollRef = useRef(null);
  const [mobileCanScrollLeft, setMobileCanScrollLeft] = useState(false);
  const [mobileCanScrollRight, setMobileCanScrollRight] = useState(false);
  const [canGoLeft, setCanGoLeft] = useState(false);
  const [canGoRight, setCanGoRight] = useState(false);
  
  const getCategoryImage = (category) => {
    // Always prefer admin-set category image
    if (category.image) {
      return category.image;
    }
    // Fallback to first product image if no category image set
    const categoryProducts = products.filter(p => 
      p.model_category?.toLowerCase() === category.id.toLowerCase()
    );
    if (categoryProducts.length > 0 && categoryProducts[0].images?.[0]) {
      return categoryProducts[0].images[0];
    }
    return '';
  };

  const checkMobileScroll = useCallback(() => {
    if (mobileScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = mobileScrollRef.current;
      setMobileCanScrollLeft(scrollLeft > 10);
      setMobileCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  }, []);

  const checkDesktopScroll = useCallback(() => {
    if (desktopScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = desktopScrollRef.current;
      setCanGoLeft(scrollLeft > 10);
      setCanGoRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  }, []);
  
  useEffect(() => {
    checkMobileScroll();
    checkDesktopScroll();
    const mRef = mobileScrollRef.current;
    const dRef = desktopScrollRef.current;
    if (mRef) mRef.addEventListener('scroll', checkMobileScroll);
    if (dRef) {
      dRef.addEventListener('scroll', checkDesktopScroll);
      setTimeout(checkDesktopScroll, 500);
    }
    window.addEventListener('resize', checkDesktopScroll);
    return () => {
      if (mRef) mRef.removeEventListener('scroll', checkMobileScroll);
      if (dRef) dRef.removeEventListener('scroll', checkDesktopScroll);
      window.removeEventListener('resize', checkDesktopScroll);
    };
  }, [checkMobileScroll, checkDesktopScroll, categories]);

  const scrollMobile = (direction) => {
    if (mobileScrollRef.current) {
      const scrollAmount = mobileScrollRef.current.clientWidth * 0.8;
      mobileScrollRef.current.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
      setTimeout(checkMobileScroll, 400);
    }
  };

  const scrollDesktop = (direction) => {
    if (desktopScrollRef.current) {
      const containerWidth = desktopScrollRef.current.clientWidth;
      const colWidth = containerWidth / 8;
      desktopScrollRef.current.scrollBy({ left: direction === 'left' ? -colWidth : colWidth, behavior: 'smooth' });
      setTimeout(checkDesktopScroll, 400);
    }
  };

  const desktopColumns = [];
  for (let i = 0; i < categories.length; i += 2) {
    desktopColumns.push(categories.slice(i, i + 2));
  }
  
  return (
    <section className="py-4 md:py-6 bg-[#FEFAF3]/80 relative" data-testid="shop-by-category">
      <div className="max-w-7xl mx-auto px-2 md:px-4 lg:px-6">
        
        {/* DESKTOP */}
        <div className="hidden md:block relative">
          {canGoLeft && (
            <button onClick={() => scrollDesktop('left')} className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-white/90 shadow-lg border border-gray-200 hover:bg-white" aria-label="Scroll left">
              <ChevronLeft className="h-5 w-5 text-[#1a3a5c]" />
            </button>
          )}
          {canGoRight && (
            <button onClick={() => scrollDesktop('right')} className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-white/90 shadow-lg border border-gray-200 hover:bg-white" aria-label="Scroll right">
              <ChevronRight className="h-5 w-5 text-[#1a3a5c]" />
            </button>
          )}
          
          <div ref={desktopScrollRef} className="overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch' }}>
            <div className="flex gap-3" style={{ width: `${(desktopColumns.length / 8) * 100}%`, minWidth: desktopColumns.length > 8 ? `${(desktopColumns.length / 8) * 100}%` : '100%' }}>
              {desktopColumns.map((col, colIndex) => (
                <div key={colIndex} className="flex flex-col gap-3" style={{ width: `calc(${100 / desktopColumns.length}% - ${((desktopColumns.length - 1) * 12) / desktopColumns.length}px)`, flexShrink: 0, scrollSnapAlign: 'start' }}>
                  {col.map((category) => (
                    <Link key={category.id} to={category.link} className="group block" data-testid={`category-${category.id}`}>
                      <div className="aspect-square rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100">
                        <img src={getCategoryImage(category)} alt={category.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" style={category.focal_x != null ? { objectPosition: `${category.focal_x}% ${category.focal_y ?? 50}%` } : undefined} loading="lazy" />
                      </div>
                      <h3 className="mt-2 text-center text-sm font-medium text-[#1a3a5c] group-hover:text-[#C9A967] transition-colors truncate">{category.name}</h3>
                    </Link>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* MOBILE */}
        <div className="md:hidden relative">
          {mobileCanScrollLeft && (
            <button onClick={() => scrollMobile('left')} className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-1.5 rounded-full bg-white/90 shadow-lg border border-gray-200" aria-label="Scroll left">
              <ChevronLeft className="h-4 w-4 text-[#1a3a5c]" />
            </button>
          )}
          {mobileCanScrollRight && (
            <button onClick={() => scrollMobile('right')} className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-1.5 rounded-full bg-white/90 shadow-lg border border-gray-200" aria-label="Scroll right">
              <ChevronRight className="h-4 w-4 text-[#1a3a5c]" />
            </button>
          )}
          
          <div ref={mobileScrollRef} className="overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}>
            <div className="flex gap-2 px-1 pb-2" style={{ width: 'max-content' }}>
              {Array.from({ length: Math.ceil(categories.length / 4) }).map((_, groupIndex) => {
                const groupItems = categories.slice(groupIndex * 4, groupIndex * 4 + 4);
                return (
                  <div key={groupIndex} className="grid grid-cols-2 gap-2" style={{ width: 'calc(50vw - 16px)', minWidth: '160px' }}>
                    {groupItems.map((category) => (
                      <Link key={category.id} to={category.link} className="group block" data-testid={`category-${category.id}`}>
                        <div className="aspect-square rounded-lg overflow-hidden bg-white shadow-sm border border-gray-100">
                          <img src={getCategoryImage(category)} alt={category.name} className="w-full h-full object-cover" style={category.focal_x != null ? { objectPosition: `${category.focal_x}% ${category.focal_y ?? 50}%` } : undefined} loading="lazy" draggable="false" />
                        </div>
                        <h3 className="mt-1 text-center text-xs font-medium text-[#1a3a5c] truncate">{category.name}</h3>
                      </Link>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        
      </div>
    </section>
  );
};
